import { type StructuredContentDocument } from './types';

export const isStructuredContent = (section: unknown): section is StructuredContentDocument => {
  return (
    section !== null &&
    typeof section !== 'undefined' &&
    typeof section === 'object' &&
    'type' in section &&
    'version' in section &&
    'props' in section
  );
};

/**
 * Recursive function to traverse a structured content document and perform a custom operation on each node.
 *
 * ## Usage Examples:
 *
 * ```typescript
 * // Find first TableOfContents node (with early termination)
 * let found: StructuredContentDocument | null = null;
 * traverseStructuredContent(doc, (node) => {
 *   if (node.type === 'TableOfContents' && !found) {
 *     found = node;
 *     return false; // Stop traversal
 *   }
 *   return true; // Continue traversal
 * });
 * // Collect all headings with level 2
 * const h2Headings: StructuredContentDocument[] = [];
 * traverseStructuredContent(doc, (node) => {
 *   if (node.type === 'ContentHeading' && node.props?.level === 2) {
 *     h2Headings.push(node);
 *   }
 *   return true; // Continue traversal
 * });
 * ```
 *
 * @param doc - Structured content document to traverse
 * @param visitor - Function called for each node. Return true to continue, false to stop
 * @returns void
 */
function traverseStructuredContent(
  doc: StructuredContentDocument,
  visitor: (node: StructuredContentDocument) => boolean,
): void {
  const traverseNode = (node: StructuredContentDocument): boolean => {
    // Call visitor function
    const shouldContinue = visitor(node);
    if (!shouldContinue) {
      return false; // Stop traversal
    }

    const children = node.props?.children;
    if (!children) return true;

    const childrenArray = Array.isArray(children) ? children : [children];

    for (const child of childrenArray) {
      if (isStructuredContent(child)) {
        const shouldContinue = traverseNode(child);
        if (!shouldContinue) {
          return false; // Propagate stop signal
        }
      }
    }

    return true;
  };

  traverseNode(doc);
}

/**
 * Finds the first node of a specific type in a structured content document
 * @param doc - Structured content document to search through
 * @param nodeType - The type of node to search for
 * @returns The first matching node or null if no match is found
 */
export function findNodeByType(
  doc: StructuredContentDocument,
  nodeType: string,
): StructuredContentDocument | null {
  let found: StructuredContentDocument | null = null;

  traverseStructuredContent(doc, node => {
    const matches = node.type === nodeType;

    if (matches && !found) {
      found = node;
      return false; // Stop traversal to return found element
    }
    return true; // Continue traversal
  });

  return found;
}

/**
 * Finds all nodes of a specific type in a structured content document
 * @param doc - Structured content document to search through
 * @param nodeType - The type of node to search for
 * @returns Array of all matching nodes (empty array if none found)
 */
export function findAllNodesByType(
  doc: StructuredContentDocument,
  nodeType: string,
): StructuredContentDocument[] {
  const results: StructuredContentDocument[] = [];

  traverseStructuredContent(doc, node => {
    const matches = node.type === nodeType;

    if (matches) {
      results.push(node);
    }
    return true; // Continue traversal to find all matches
  });

  return results;
}
